import React, { useState } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../../ui/chart';
import { TruncationTooltip } from '../../../../common/TruncationTooltip';
import { useIsTruncated } from '../../../../../lib/useIsTruncated';
import type { VideoResult, AnalysisResult } from '../../types';
import { RC, safetyChartConfig } from '../../constants';
import { TranscriptRow } from '../../ChannelDashboard';
import { ChannelAverageDonut } from './ChannelAverageDonut';
import { SentimentDistributionChart } from './SentimentDistributionChart';

interface SentimentBarDatum {
  name: string;
  Positive: number;
  Neutral: number;
  Negative: number;
  Toxic: number;
}

interface SafetyCompareDatum {
  fullTitle: string;
  commentSafety: number;
  transcriptSafety: number | null;
  flag: string;
  views: string;
  comments: number;
}

interface RadarDatum {
  subject: string;
  value: number;
  fullMark: number;
}

/** Connected segmented row — shared borders, rounded only at the outer ends
 *  (Figma: the Avg Positive/Neutral/Negative/Toxic strip, reused as-is for
 *  the Safe/Moderate/Risky breakdown so both stat rows read consistently). */
function ConnectedStatRow({
  items,
}: {
  items: { key: string; label: string; value: string; color?: string }[];
}) {
  return (
    <div style={{ display: 'flex' }}>
      {items.map((it, i) => {
        const first = i === 0;
        const last = i === items.length - 1;
        return (
          <div
            key={it.key}
            style={{
              flex: 1,
              background: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: first ? '8px 0 0 8px' : last ? '0 8px 8px 0' : 0,
              marginLeft: first ? 0 : -1,
              padding: '12px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 400, color: '#737373' }}>{it.label}</div>
            <div style={{ fontSize: 20, fontWeight: 500, color: it.color ?? '#171717' }}>
              {it.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** One video row in the Safety Profile pill-bar list. The name column is a
 *  fixed width so long titles CSS-truncate — useIsTruncated (a hook, so this
 *  has to be its own component rather than inlined in the parent's .map())
 *  gates a tooltip showing the full title, only when it's actually cut off. */
function SafetyCompareRow({ d }: { d: SafetyCompareDatum }) {
  const [nameRef, isTruncated] = useIsTruncated<HTMLDivElement>(d.fullTitle);
  const nameEl = (
    <div
      ref={nameRef}
      style={{
        width: 214,
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 500,
        color: '#404040',
        textAlign: 'right',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {d.fullTitle}
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <TruncationTooltip
        isTruncated={isTruncated}
        content={d.fullTitle}
        contentClassName="max-w-[280px] text-left whitespace-normal"
      >
        {nameEl}
      </TruncationTooltip>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
        <div style={{ background: '#ECEDF0', height: 9, borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              background: '#6C09F7',
              height: 9,
              borderRadius: 999,
              width: `${Math.min(Math.max(d.commentSafety, 0), 100)}%`,
            }}
          />
        </div>
        <div style={{ background: '#ECEDF0', height: 9, borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              background: d.transcriptSafety == null ? 'var(--border)' : '#F37AED',
              height: 9,
              borderRadius: 999,
              width: `${Math.min(Math.max(d.transcriptSafety ?? 0, 0), 100)}%`,
            }}
          />
        </div>
      </div>
      <div style={{ width: 46, flexShrink: 0, textAlign: 'right', fontSize: 16, fontWeight: 500, color: '#404040' }}>
        {d.commentSafety}
      </div>
    </div>
  );
}

interface ChannelAnalysisCardProps {
  card: React.CSSProperties;
  summary: AnalysisResult['summary'];
  videos: VideoResult[];
  isInstagram: boolean;
  barData: SentimentBarDatum[];
  safetyCompareData: SafetyCompareDatum[];
  radarData: RadarDatum[];
}

export function ChannelAnalysisCard({
  card,
  summary,
  videos,
  isInstagram,
  barData,
  safetyCompareData,
  radarData,
}: ChannelAnalysisCardProps) {
  const [activeChart, setActiveChart] = useState<'sentiment' | 'risk' | 'radar' | 'transcripts'>(
    'sentiment',
  );
  // Labels reordered to match Figma's tab order — the CONTENT keyed 'risk' is
  // the Comment/Transcript safety bars (Figma's "Safety Profile" tab) and the
  // content keyed 'radar' is the 6-axis radar (Figma's "Risk Scores" tab); the
  // two were previously mislabeled relative to each other.
  const chartTabs = [
    { key: 'sentiment', label: 'Sentiment' },
    { key: 'radar', label: 'Risk Scores' },
    { key: 'risk', label: 'Safety Profile' },
    { key: 'transcripts', label: 'Transcription' },
  ] as const;

  return (
      <div style={{ ...card, padding: 24 }}>
        {/* Header: title + subtitle (left) + segmented tabs (right) — Figma 1741:55609 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 500, color: '#171717' }}>Channel Analysis</div>
            <div style={{ fontSize: 14, fontWeight: 400, color: '#737373', marginTop: 4 }}>
              Sentiment, risk &amp; safety insights across {videos.length} videos
            </div>
          </div>
          {/* Segmented control — Figma: #F4F4F2 track, white active pill + shadow */}
          <div
            style={{
              display: 'inline-flex',
              gap: 6,
              padding: 4,
              background: '#F4F4F2',
              border: '1px solid #E5E5E5',
              borderRadius: 24,
              maxWidth: '100%',
              flexWrap: 'wrap',
            }}
          >
          {chartTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveChart(t.key)}
              style={{
                padding: '4px 20px',
                borderRadius: 24,
                border: 'none',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                background: activeChart === t.key ? '#FFFFFF' : 'transparent',
                color: activeChart === t.key ? '#171717' : '#737373',
                boxShadow:
                  activeChart === t.key
                    ? '0px 2px 5px rgba(0,0,0,0.06), 0px 1px 2px rgba(0,0,0,0.1)'
                    : 'none',
              }}
            >
              {t.label}
            </button>
          ))}
          </div>
        </div>

        {/* ── Sentiment tab ── */}
        {activeChart === 'sentiment' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Stat cards — Figma: connected segmented row (shared borders, rounded ends) */}
            <ConnectedStatRow
              items={[
                { key: 'positive', label: 'Avg. Positive', val: summary.avg_positive_pct },
                { key: 'neutral', label: 'Avg. Neutral', val: summary.avg_neutral_pct },
                { key: 'negative', label: 'Avg. Negative', val: summary.avg_negative_pct },
                { key: 'toxic', label: 'Avg. Toxic', val: summary.avg_toxic_pct },
              ]
                .filter((s) => s.val != null)
                .map((s) => ({ key: s.key, label: s.label, value: `${(s.val ?? 0).toFixed(1)}%` }))}
            />
            {/* Two separate equal-width cards — Channel Average donut | Sentiment
                Distribution (Figma 2165:108354): 24px gap, 22px padding, 12px radius. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'stretch' }}>
              <div style={{ border: '1px solid #E5E5E5', borderRadius: 12, padding: 22, minWidth: 0 }}>
                <ChannelAverageDonut summary={summary} />
              </div>
              <div style={{ border: '1px solid #E5E5E5', borderRadius: 12, padding: 22, minWidth: 0 }}>
                <SentimentDistributionChart barData={barData} />
              </div>
            </div>
          </div>
        )}

        {/* ── Risk & Safety tab ── */}
        {activeChart === 'risk' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div>
                <div className="text-heading-h6 text-foreground" style={{ marginBottom: 2 }}>
                  Comment Safety vs. Transcript Safety
                </div>
                <div className="text-caption-medium text-muted-foreground">
                  100 = fully safe · sorted safest last · purple = comment safety · pink =
                  transcript safety
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: '#6C09F7',
                      display: 'inline-block',
                    }}
                  />
                  Comment Safety
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: '#F37AED',
                      display: 'inline-block',
                    }}
                  />
                  Transcript Safety
                </span>
              </div>
            </div>
            {/* Pill bars — Figma: each video gets two full-width, fully-rounded
                tracks (comment safety above transcript safety), a light-gray
                unfilled remainder, and one score number (comment safety) at
                the end. Plain CSS, not a recharts BarChart — matches the
                design's own literal structure and avoids fighting recharts'
                corner-radius model into a pill shape. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {safetyCompareData.map((d, i) => (
                <SafetyCompareRow key={i} d={d} />
              ))}
            </div>
            {/* Flag breakdown — Figma: plain label + count, same connected-row
                style as the Sentiment tab's Avg stats (no percentage line or
                fill bar). */}
            <ConnectedStatRow
              items={(['Safe', 'Moderate', 'Risky'] as const).map((f) => ({
                key: f,
                label: f,
                value: String(videos.filter((v) => v.flag === f).length),
                color: RC(f).text,
              }))}
            />
          </div>
        )}

        {/* ── Safety Radar tab ── */}
        {activeChart === 'radar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div className="text-heading-h6 text-foreground" style={{ marginBottom: 2 }}>
                Channel Safety Profile
              </div>
              <div className="text-caption-medium text-muted-foreground">
                6-dimensional radar — higher score = better on all axes
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 210px',
                gap: 20,
                alignItems: 'center',
              }}
            >
              <ChartContainer config={safetyChartConfig} className="h-[260px] w-full">
                <RadarChart data={radarData} margin={{ top: 14, right: 28, bottom: 14, left: 28 }}>
                  <PolarGrid gridType="polygon" stroke="#D4D4D4" strokeWidth={1.5} />
                  <PolarAngleAxis
                    dataKey="subject"
                    tick={{ fontSize: 16, fill: 'var(--foreground)', fontWeight: 600 }}
                  />
                  <Radar
                    isAnimationActive={false}
                    name="Channel"
                    dataKey="value"
                    stroke="#6C09F7"
                    fill="#6C09F7"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    dot={(props: { cx?: number; cy?: number }) => (
                      <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={4}
                        fill="#5A07CE"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    )}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                </RadarChart>
              </ChartContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {radarData.map((d) => {
                  const tone = RC(d.value >= 70 ? 'Safe' : d.value >= 45 ? 'Moderate' : 'Risky');
                  return (
                    <div
                      key={d.subject}
                      style={{
                        background: tone.bg,
                        border: `1px solid ${tone.border}`,
                        borderRadius: 10,
                        padding: '9px 11px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 5,
                        }}
                      >
                        <span className="text-body-md-medium text-foreground">
                          {d.subject}
                        </span>
                        <span style={{ fontSize: 16, fontWeight: 600, color: tone.text }}>{d.value}</span>
                      </div>
                      <div
                        style={{
                          height: 6,
                          background: tone.border,
                          borderRadius: 3,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${d.value}%`,
                            background: tone.text,
                            borderRadius: 3,
                            transition: 'width 0.8s ease',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Transcripts tab ── */}
        {activeChart === 'transcripts' && (
          <div>
            <div className="text-heading-h6 text-foreground" style={{ marginBottom: 3 }}>
              Video Transcripts
            </div>
            <div className="text-caption-medium text-muted-foreground" style={{ marginBottom: 16 }}>
              Full auto-generated captions for all analyzed videos
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {videos.map((v, i) => (
                <TranscriptRow key={v.video_id || i} video={v} index={i} isInstagram={isInstagram} />
              ))}
            </div>
          </div>
        )}
      </div>
  );
}
